import type { EError, RestAPI } from "@sc07-canvas/lib";
import { Debug } from "@sc07-canvas/lib/debug";
import createClient from "openapi-fetch";
import { toast } from "react-toastify";
import { createQueryHook } from "swr-openapi";

import { Renderer } from "./renderer";

let _renderer: Renderer;

/**
 * Get the renderer instance or create one
 * @returns
 */
export const getRenderer = (): Renderer => {
  if (_renderer) return _renderer;

  _renderer = Renderer.create();
  return _renderer;
};

Debug._getRenderer = getRenderer;

export const oapi = createClient<RestAPI.paths>({
  baseUrl: new URL("/api", window.location.origin).toString(),
});

export const useQuery = createQueryHook(oapi, "api");

export const api = async <T = unknown, Error = string>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" = "GET",
  body?: unknown,
): Promise<{
  status: number;
  data:
    | ({ success: true } & T)
    | { success: false; error: Error; error_message?: string };
}> => {
  const req = await fetch(endpoint, {
    method,
    credentials: "include",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: JSON.stringify(body),
  });

  let data: any;

  try {
    data = await req.json();
  } catch (_e) {
    /* empty */
  }

  return {
    status: req.status,
    data,
  };
};

export type PickMatching<T, V> = {
  [K in keyof T as T[K] extends V ? K : never]: T[K];
};

export type ExtractMethods<T> = PickMatching<T, (...args: any) => any>;

export type EnforceObjectType<T> = <V extends { [k: string]: T }>(
  v: V,
) => { [k in keyof V]: T };

type APIResponse = Awaited<ReturnType<typeof api>>;

interface HandleError {
  (error: EError | Error): void;
  (api_response: APIResponse): void;
}

export const handleError: HandleError = (error) => {
  if (error instanceof Error) {
    console.error(error);
    toast.error(error.toString());
    return;
  }

  toast.error(
    `Error: [${error.status}] ` +
      ("error" in error.data ? error.data.error : "Unknown Error"),
  );
};
